import { useCallback, useEffect, useRef, useState } from 'react'

export function useVoiceInput({ lang = 'zh-TW', continuous = true, interimResults = true } = {}) {
  const [transcript, setTranscript] = useState('')
  const [isListening, setIsListening] = useState(false)
  const recognitionRef = useRef(null)

  const isBrowser = typeof window !== 'undefined'
  const isSecureOrLocalhost =
    isBrowser &&
    (window.isSecureContext ||
      ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname))

  const supported =
    isSecureOrLocalhost &&
    !!(window.SpeechRecognition || window.webkitSpeechRecognition)

  const [error, setError] = useState(
    isBrowser && !isSecureOrLocalhost
      ? '手機請用 HTTPS 開啟，HTTP 會被瀏覽器擋住麥克風'
      : null
  )

  useEffect(() => () => recognitionRef.current?.stop(), [])

  const start = useCallback(() => {
    if (!supported) {
      setError(
        isSecureOrLocalhost
          ? '這個瀏覽器不支援語音輸入，請改用打字喔'
          : '手機請用 HTTPS 開啟，HTTP 會被瀏覽器擋住麥克風'
      )
      return
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    let rec = recognitionRef.current
    if (!rec) {
      rec = new SR()
      recognitionRef.current = rec
    }
    rec.lang = lang
    rec.continuous = continuous
    rec.interimResults = interimResults
    rec.onresult = (e) => {
      let t = ''
      for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript
      setTranscript(t)
    }
    rec.onerror = (e) => setError(e.error === 'not-allowed' ? '麥克風權限被拒絕' : '聽不清楚，請再試一次')
    rec.onend = () => setIsListening(false)
    setTranscript('')
    setError(null)
    setIsListening(true)
    try {
      rec.start()
    } catch {
      // 已在錄音中，忽略
    }
  }, [lang, continuous, interimResults, supported, isSecureOrLocalhost])

  const stop = useCallback(() => {
    recognitionRef.current?.stop()
    setIsListening(false)
  }, [])

  return { transcript, isListening, error, supported, start, stop }
}
